/**
 * Enquiries inbox for the realtor Home dashboard: the open-enquiry count and
 * the first few open enquiries in the order the endpoint returns them, each
 * linking to its chat on the Enquiries page.
 */

import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEnquiries } from "@/features/realtor/hooks/use-realtor-crm";
import type { EnquirySummary } from "@/features/realtor/services/realtor-crm-api.service";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { SectionHeading } from "./section-heading";

const INBOX_LIMIT = 3;

export function EnquiriesInbox() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const query = useEnquiries({ status: "open" });
	const enquiries = query.data ?? [];

	return (
		<Card
			className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
			data-testid="enquiries-inbox"
		>
			<CardContent className="p-6 space-y-4">
				<header className="flex items-end justify-between gap-3">
					<SectionHeading
						eyebrow={t("realtor.dashboard.enquiries.eyebrow")}
						title={
							<>
								{t("realtor.dashboard.enquiries.title")}
								{query.isSuccess && (
									<span
										className="ml-2 align-middle rounded-full bg-dashboard-surface px-2 py-0.5 text-xs not-italic text-dashboard-muted"
										data-testid="enquiries-open-count"
									>
										{t("realtor.dashboard.enquiries.openCount", {
											count: enquiries.length,
										})}
									</span>
								)}
							</>
						}
						size={24}
					/>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 px-3 text-xs text-dashboard-muted hover:text-dashboard-text hover:bg-dashboard-surface"
						onClick={() => navigate("/realtor/enquiries")}
					>
						{t("realtor.dashboard.viewAll")}
					</Button>
				</header>

				{query.isPending ? (
					<Skeleton className="h-16 w-full rounded-lg bg-dashboard-surface" />
				) : query.isError ? (
					<div
						role="alert"
						className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-dashboard-border px-3 py-4"
					>
						<p className="text-xs text-dashboard-muted">
							{t("realtor.dashboard.enquiries.loadFailed")}
						</p>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-7 gap-1.5 px-2 text-xs"
							onClick={() => query.refetch()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.dashboard.retry")}
						</Button>
					</div>
				) : enquiries.length === 0 ? (
					<div className="rounded-lg border border-dashed border-dashboard-border bg-dashboard-bg/50 px-3 py-6 text-center">
						<p className="text-xs text-dashboard-muted">
							{t("realtor.dashboard.enquiries.empty")}
						</p>
					</div>
				) : (
					<ul className="space-y-2">
						{enquiries.slice(0, INBOX_LIMIT).map((summary) => (
							<EnquiryRow
								key={summary.enquiry.id}
								summary={summary}
								onOpen={() =>
									navigate(
										`/realtor/enquiries?open=${encodeURIComponent(summary.enquiry.id)}`,
									)
								}
							/>
						))}
					</ul>
				)}
			</CardContent>
		</Card>
	);
}

function EnquiryRow({
	summary,
	onOpen,
}: {
	summary: EnquirySummary;
	onOpen: () => void;
}) {
	const { t, i18n } = useTranslation();
	const { enquiry, firstMessage } = summary;
	return (
		<li className="flex items-start gap-3 rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2.5">
			<div className="flex-1 min-w-0 space-y-0.5">
				<p className="flex items-center gap-2 text-sm font-medium text-dashboard-text">
					<span className="truncate">{enquiry.name}</span>
					<span className="shrink-0 rounded-full bg-dashboard-surface px-2 py-0.5 text-[11px] font-normal text-dashboard-muted">
						{t(`realtor.enquiries.channels.${enquiry.channel}`)}
					</span>
				</p>
				{firstMessage && (
					<p className="truncate text-xs text-dashboard-muted">
						{firstMessage.body}
					</p>
				)}
				<time
					dateTime={enquiry.createdAt}
					className="block text-[11px] text-dashboard-muted"
				>
					{formatRelativeTime(enquiry.createdAt, i18n.language)}
				</time>
			</div>
			<Button
				type="button"
				size="sm"
				className="h-8 shrink-0 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-[8px]"
				aria-label={t("realtor.dashboard.enquiries.openChatFor", {
					name: enquiry.name,
				})}
				onClick={onOpen}
			>
				{t("realtor.dashboard.enquiries.openChat")}
			</Button>
		</li>
	);
}
